import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Briefcase, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, isPast, differenceInDays } from 'date-fns';
import { eventTeamApiService } from '../../../services/eventTeamApi';
import type { EventProject, EventProjectListParams, EventTag, EventTeamMember } from '../../../types/eventTeam';
import { EVENT_PROJECT_STATUSES } from '../../../types/eventTeam';
import type { EventTeamPerms } from '../../../utils/eventTeamPermissions';
import { EventStatusBadge } from '../EventStatusBadge';
import { EventPriorityBadge } from '../EventPriorityBadge';
import { EventFilters, EMPTY_EVENT_FILTERS, type EventFiltersState } from '../EventFilters';
import { EventProjectModal } from '../EventProjectModal';
import { useDebounce } from '../../../hooks/useDebounce';
import { SectionHeader } from '../../ui/SectionHeader';
import { EmptyState } from '../../ui/EmptyState';
import { SkeletonLoader } from '../../SkeletonLoader';

const PAGE_SIZE = 50;

export interface EventProjectsPanelProps {
  perms: EventTeamPerms;
  onOpenProject: (id: string) => void;
}

function dueDateStyle(dueDate: number, status: string): string {
  if (status === 'completed' || status === 'cancelled') return 'text-glass-400';
  const d = new Date(dueDate);
  if (isPast(d)) return 'text-red-400';
  if (differenceInDays(d, new Date()) <= 3) return 'text-amber-400';
  return 'text-glass-400';
}

/** Projects list (guide §1.6) — filterable, sortable, paginated `{results,total}`. */
export const EventProjectsPanel = ({ perms, onOpenProject }: EventProjectsPanelProps) => {
  const [filters, setFilters] = useState<EventFiltersState>({ ...EMPTY_EVENT_FILTERS });
  const debouncedSearch = useDebounce(filters.search, 300);

  const [projects, setProjects] = useState<EventProject[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tags, setTags] = useState<EventTag[]>([]);
  const [members, setMembers] = useState<EventTeamMember[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  /** Overall count from the dedicated `GET /events/projects/count` endpoint. */
  const [overallCount, setOverallCount] = useState<number | null>(null);

  useEffect(() => {
    eventTeamApiService.listTags().then(setTags).catch(() => setTags([]));
    eventTeamApiService
      .listTeamMembers({ limit: 200 })
      .then((res) => setMembers(res.results))
      .catch(() => setMembers([]));
    eventTeamApiService
      .countProjects()
      .then((res) => setOverallCount(res.count))
      .catch(() => setOverallCount(null));
  }, []);

  const queryParams = useMemo((): EventProjectListParams => {
    return {
      status: filters.status || undefined,
      category: filters.category || undefined,
      assignee: filters.assignee || undefined,
      tag_id: filters.tagId || undefined,
      search: debouncedSearch || undefined,
      priority_min: filters.priorityMin || undefined,
      priority_max: filters.priorityMax || undefined,
      sort_by: filters.sortBy || 'created_at',
      sort_order: filters.sortOrder,
    };
  }, [filters, debouncedSearch]);

  const load = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      setError(null);
      try {
        const res = await eventTeamApiService.listProjects({
          ...queryParams,
          page: targetPage,
          limit: PAGE_SIZE,
        });
        setProjects(res.results);
        setTotal(res.total);
        setPage(targetPage);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load projects');
      } finally {
        setLoading(false);
      }
    },
    [queryParams],
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <SectionHeader
        as="h2"
        title="Projects"
        icon={Briefcase}
        iconClassName="text-blue-400"
        count={overallCount != null && overallCount !== total ? `${total} of ${overallCount}` : total}
        className="mb-0 flex-wrap"
        actions={
          perms.canCreate ? (
            <button type="button" onClick={() => setShowCreate(true)} className="btn-primary">
              <Plus className="w-4 h-4" />
              New Project
            </button>
          ) : undefined
        }
      />

      <EventFilters
        variant="projects"
        filters={filters}
        onChange={setFilters}
        statuses={EVENT_PROJECT_STATUSES}
        tags={tags}
        members={members}
      />

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="glass-card p-3 md:p-4 space-y-2">
              <SkeletonLoader className="h-4 w-56 max-w-full" />
              <SkeletonLoader className="h-3 w-full max-w-md" />
              <div className="flex gap-2">
                <SkeletonLoader className="h-5 w-20 rounded-full" />
                <SkeletonLoader className="h-5 w-10 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="alert alert-danger" role="alert">
          <AlertCircle />
          <span className="flex-1">{error}</span>
        </div>
      ) : projects.length === 0 ? (
        <EmptyState icon={Briefcase} title="No projects match the current filters." />
      ) : (
        <div className="space-y-2">
          {projects.map((project) => (
            <button
              key={project.id}
              type="button"
              onClick={() => onOpenProject(project.id)}
              className="w-full glass-card glass-card-interactive p-3 md:p-4 text-left"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-semibold text-white block truncate">{project.title}</span>
                  {project.description && (
                    <span className="text-xs text-glass-400 line-clamp-1">{project.description}</span>
                  )}
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap text-xs text-glass-500">
                    <EventStatusBadge status={project.status} type="project" />
                    <EventPriorityBadge priority={project.priority} />
                    {project.category && (
                      <span className="badge bg-cyan-500/15 text-cyan-300 border-cyan-500/25">{project.category}</span>
                    )}
                    {project.due_date && (
                      <span className={dueDateStyle(project.due_date, project.status)}>
                        Due {format(new Date(project.due_date), 'MMM d, yyyy')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => void load(page - 1)}
            disabled={page <= 1 || loading}
            className="btn-icon"
            title="Previous page"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-glass-400 tabular-nums">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => void load(page + 1)}
            disabled={page >= totalPages || loading}
            className="btn-icon"
            title="Next page"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      <EventProjectModal
        isOpen={showCreate}
        mode="create"
        onClose={() => setShowCreate(false)}
        onSuccess={(created) => onOpenProject(created.id)}
      />
    </div>
  );
};
